import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

/**
 * /enrolled's RC1 card, over a photo of a home. With an annual premium it shows the estimate, framed as one that a
 * full quote firms up (the CTA below it), and one-sided: it never compares against what the customer pays now.
 * Without one (no RC1 value, or not confident Kin is competitive) it leads with benefits instead, so there's no
 * figure to anchor on.
 *
 * Copy is from the design mock (screen 7, Variants A and B); run it through kin-brand-messaging before this ships.
 */
@Component({
  selector: 'app-rc1-estimate-card',
  imports: [CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /*
     * Photo frame radius is longest side ÷ 20 (5% of the /enrolled card it fills; see .promo's container), capped at
     * the outer card's 24px so the nested corners never curve harder than the card around them. Neutral-1000 shows
     * if the photo doesn't load, so the white type keeps its contrast.
     */
    :host {
      position: relative; display: block; overflow: hidden; isolation: isolate;
      border-radius: clamp(16px, 5cqi, var(--kin-radius-card));
      background: var(--kin-neutral-1000); color: var(--kin-white);
    }
    .photo { position: absolute; inset: 0; z-index: -1; width: 100%; height: 100%; object-fit: cover; }

    /*
     * Neutral-1000 scrim, heaviest under the type: white on it stays above 7:1 over the brightest photo, and
     * Yellow-600 above 9:1
     */
    .content {
      display: flex; flex-direction: column; justify-content: flex-end; min-height: 240px; padding: 28px;
      box-sizing: border-box; container-type: inline-size;
      background: linear-gradient(to top, rgba(18, 18, 15, 0.9), rgba(18, 18, 15, 0.7) 45%, rgba(18, 18, 15, 0.2));
    }
    /* The global p rule sets its own color, so the card's paragraphs take the white back */
    .address, .note, .headline { color: inherit; }
    /* Body Small / 600 */
    .address { margin: 0 0 8px; font-size: 14px; font-weight: 600; line-height: 1.5; }
    .figure { margin: 0; }
    /* Body / 500 */
    .label { font-size: 16px; font-weight: 500; line-height: 1.5; }
    /* Display in Yellow-600: the one number on the page */
    .amount {
      margin: 0 0 8px; font-size: 56px; font-weight: 700; line-height: 1.05; letter-spacing: -2px;
      font-variant-numeric: tabular-nums; color: var(--kin-yellow-600);
    }
    /* Body Small */
    .note { margin: 0; font-size: 14px; line-height: 1.5; text-wrap: pretty; }

    /* No figure: Heading 2 in white (above 8:1 on the scrim) */
    .headline {
      margin: 0 0 12px; max-width: 420px; font-size: 28px; font-weight: 600; line-height: 1.15; letter-spacing: -0.5px;
      text-wrap: balance;
    }
    /*
     * Body Small / 600 in white on a translucent white pill, muted into the photo as in the design. The pills sit in
     * the bottom third, where the scrim is at least 77%: even over pure white the pill comes out no lighter than
     * #666, so white on it stays above 5.8:1. The blur keeps a busy photo from showing through the label. The color
     * and size are set here since the global li rule sets its own.
     *
     * The row never stacks. Sized in em, it scales with the card (100cqi is .content's width) from 14px down to
     * Caption's 12px, where the full labels need about 370px.
     */
    .perks {
      display: flex; flex-wrap: wrap; gap: 0.57em; margin: 0; padding: 0; list-style: none;
      font-size: clamp(12px, 3.3cqi - 0.2px, 14px);
    }
    .perks li {
      padding: 0.43em 1em; font-size: inherit; font-weight: 600; line-height: 1.5; color: var(--kin-white);
      background: rgba(255, 255, 255, 0.16); border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: var(--kin-radius-pill); backdrop-filter: blur(8px);
    }

    @media (max-width: 600px) {
      .content { min-height: 220px; padding: 20px 16px; }
      .amount { font-size: 44px; letter-spacing: -1.5px; }
      .headline { font-size: 24px; }
    }
  `,
  template: `
    <img class="photo" [src]="imageUrl()" alt="" />
    <div class="content">
      @if (address()) { <p class="address">{{ address() }}</p> }
      @if (annualPremium(); as annualPremium) {
        <dl class="figure">
          <dt class="label">Estimated annual premium</dt>
          <dd class="amount">{{ annualPremium | currency: 'USD' : 'symbol' : '1.0-0' }}</dd>
        </dl>
        <p class="note">Just an estimate. A full quote takes a few more details.</p>
      } @else {
        <p class="headline">Coverage built for Texas homes.</p>
        <!-- role="list": Safari drops list semantics from an unstyled list -->
        <ul class="perks" role="list">
          <li>Quote in minutes</li>
        </ul>
      }
    </div>
  `,
})
export class Rc1EstimateCardComponent {
  /** One line, e.g. "4410 Pecan Grove Dr, Katy, TX 77494"; left off when empty */
  readonly address = input.required<string>();
  /** Whole dollars a year, or null to show the benefits without a figure */
  readonly annualPremium = input.required<number | null>();
  readonly imageUrl = input.required<string>();
}
